import React from 'react';
import { 
  GraduationCap, 
  Clock, 
  Gift, 
  ChevronRight, 
  Building2, 
  Users, 
  Brain,
  Plus,
  Minus,
  Menu,
  X
} from 'lucide-react';

function App() {
  const [openFaq, setOpenFaq] = React.useState<number | null>(null);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const handleApplyClick = () => {
    window.open('https://forms.gle/je6rNurfgPmsDTau8', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm fixed w-full z-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex-shrink-0">
              <img 
                src="https://res.cloudinary.com/dbobl0gon/image/upload/v1738929723/Curacel_logo_ahkdss.svg" 
                alt="Curacel Logo" 
                className="h-8"
              />
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#" className="text-gray-700 hover:text-blue-600 font-medium">Home</a>
              <a href="#benefits" className="text-gray-700 hover:text-blue-600 font-medium">Benefits</a>
              <a href="#timeline" className="text-gray-700 hover:text-blue-600 font-medium">Timeline</a>
              <a href="#testimonials" className="text-gray-700 hover:text-blue-600 font-medium">Testimonials</a>
              <button
                onClick={handleApplyClick}
                className="bg-blue-600 text-white px-4 py-2 rounded-full font-semibold hover:bg-blue-700 transition-colors"
              >
                Apply Now
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-700 hover:text-blue-600 focus:outline-none"
              >
                {isMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1">
                <a href="#" className="block px-3 py-2 text-gray-700 hover:text-blue-600 font-medium">Home</a>
                <a href="#benefits" className="block px-3 py-2 text-gray-700 hover:text-blue-600 font-medium">Benefits</a>
                <a href="#timeline" className="block px-3 py-2 text-gray-700 hover:text-blue-600 font-medium">Timeline</a>
                <a href="#testimonials" className="block px-3 py-2 text-gray-700 hover:text-blue-600 font-medium">Testimonials</a>
                <button
                  onClick={handleApplyClick}
                  className="w-full text-left px-3 py-2 text-blue-600 font-medium"
                >
                  Apply Now
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Add padding to account for fixed navbar */}
      <div className="pt-16">
        {/* Hero Section */}
        <header className="bg-blue-600 text-white">
          <div className="container mx-auto px-4 py-16 max-w-6xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="md:w-1/2">
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                  Launch Your Career at Curacel
                </h1>
                <p className="text-xl mb-8 text-blue-100">
                  Join our Graduate Trainee & Undergraduate Internship Program and kickstart your journey in insurtech
                </p>
                <button 
                  onClick={handleApplyClick}
                  className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-blue-50 transition-colors flex items-center gap-2"
                >
                  Apply Now <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="md:w-1/2">
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80"
                  alt="Team collaboration"
                  className="rounded-lg shadow-xl"
                />
              </div>
            </div>
          </div>
        </header>

        {/* Program Overview */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="text-3xl font-bold text-center mb-12">Program Overview</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Graduate Trainee Program</h3>
                <p className="text-gray-600">3-month intensive program designed to transform fresh graduates into industry professionals</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Internship Program</h3>
                <p className="text-gray-600">3-month program for undergraduates to gain hands-on experience in insurtech</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <Brain className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Mentorship</h3>
                <p className="text-gray-600">Direct guidance from industry experts and senior professionals</p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section id="benefits" className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="text-3xl font-bold text-center mb-12">Program Benefits</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4 bg-white p-6 rounded-lg shadow-sm">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    {benefit.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section id="timeline" className="py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="text-3xl font-bold text-center mb-12">Program Timeline</h2>
            <div className="max-w-4xl mx-auto">
              <div className="relative">
                {timeline.map((item, index) => (
                  <div key={index} className="mb-8 flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                        {index + 1}
                      </div>
                      {index < timeline.length - 1 && (
                        <div className="w-0.5 h-full bg-blue-200 mt-2"></div>
                      )}
                    </div>
                    <div className="flex-1 bg-white p-6 rounded-lg shadow-sm">
                      <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="text-3xl font-bold text-center mb-12">What Our Alumni Say</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-center gap-4 mb-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <h3 className="font-semibold">{testimonial.name}</h3>
                      <p className="text-sm text-gray-600">{testimonial.role}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 italic">"{testimonial.quote}"</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm">
                  <button
                    className="w-full px-6 py-4 text-left flex items-center justify-between"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span className="font-semibold">{faq.question}</span>
                    {openFaq === index ? (
                      <Minus className="w-5 h-5 text-blue-600" />
                    ) : (
                      <Plus className="w-5 h-5 text-blue-600" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application Section */}
        <section id="application-form" className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-6xl text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Begin Your Journey?</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Take the first step towards your career in insurtech. Apply now to join our Graduate Trainee or Undergraduate Internship Program.
            </p>
            <button
              onClick={handleApplyClick}
              className="bg-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
            >
              Start Application <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-gray-400 py-8">
          <div className="container mx-auto px-4 max-w-6xl text-center">
            <p>© 2024 Curacel. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </div>
  );
}

const benefits = [
  {
    icon: <GraduationCap className="w-6 h-6 text-blue-600" />,
    title: "Professional Certification",
    description: "Earn industry-recognized certifications upon program completion"
  },
  {
    icon: <Clock className="w-6 h-6 text-blue-600" />,
    title: "Flexible Schedule",
    description: "Balance work and studies with our flexible program structure"
  },
  {
    icon: <Gift className="w-6 h-6 text-blue-600" />,
    title: "Competitive Stipend",
    description: "Receive monthly stipend to support your learning journey"
  },
  {
    title: "Real Projects",
    description: "Work on actual projects and gain hands-on experience",
    icon: <Building2 className="w-6 h-6 text-blue-600" />
  },
  {
    title: "Career Growth",
    description: "Opportunity for full-time roles upon successful completion",
    icon: <Users className="w-6 h-6 text-blue-600" />
  },
  {
    title: "Skill Development",
    description: "Access to learning resources and development programs",
    icon: <Brain className="w-6 h-6 text-blue-600" />
  }
];

const timeline = [
  {
    title: "Application & Selection",
    description: "Submit your application and go through our selection process including assessments and interviews."
  },
  {
    title: "Onboarding",
    description: "Get introduced to the team, tools, and processes you'll be working with."
  },
  {
    title: "Training Phase",
    description: "Undergo intensive training in your chosen field with hands-on projects."
  },
  {
    title: "Project Work",
    description: "Work on real projects with experienced team members."
  },
  {
    title: "Final Evaluation",
    description: "Present your work and receive final evaluation for potential full-time opportunities."
  }
];

const testimonials = [
  {
    name: "Risqat Fijabi",
    role: "Undergraduate Trainee - Product Designer",
    image: "https://res.cloudinary.com/dbobl0gon/image/upload/v1738930791/FullSizeRender_ezz6ln.png",
    quote: "As an intern, my opinions are highly valued, and I have been able to contribute significantly to the products at Curacel. I particularly enjoyed speaking to users, understanding their frustrations, and designing solutions with them in mind as a Product Designer. A great experience, I must say"
  },
  {
    name: "Oluwafikayo Adeleke",
    role: "Undergraduate Trainee - AI/ML Engineer",
    image: "https://res.cloudinary.com/dbobl0gon/image/upload/v1738930304/155A2039_copy_1_fz2mca.jpg",
    quote: "My internship at Curacel provided me with a valuable opportunity to develop my skills as a machine learning engineer. I was able to apply my knowledge to real-world projects, making meaningful contributions and gaining hands-on experience in the process."
  },
  {
    name: "Peter Adeyemi",
    role: "Intern --> Ex-COO at Curacel",
    image: "https://curacel-cdn.s3.us-east-1.amazonaws.com/Pete.jpeg",
    quote: "I joined Curacel as a Data Analyst Intern, hoping to layer on the skills I had up to that point. However, I was presented with a lot of opportunities for growth and developed into first, the Growth lead and eventually, the Chief Operating Officer. This type of growth can only be achieved in a fast growing technology company like Curacel."
  }
];

const faqs = [
  {
    question: "What are the eligibility requirements?",
    answer: "For the Graduate Trainee Program, you must have completed your bachelor's degree within the last 6 months - 2 years. For the Internship Program, you must be in your penultimate or final year of study."
  },
  {
    question: "Is the program paid?",
    answer: "Yes, both programs offer competitive stipends to support participants during their training period."
  },
  {
    question: "What is the application process?",
    answer: "The process includes online application submission, aptitude tests, technical assessments, and interviews. Successful candidates will be notified within 2-3 weeks."
  },
  {
    question: "Can I choose my department?",
    answer: "Yes, you can indicate your preferred department (Engineering, Product, Data, etc.) during the application process, but final placement will depend on your skills and our current needs."
  },
  {
    question: "Is there a possibility of full-time employment?",
    answer: "Yes, top performers may receive full-time employment offers upon successful completion of the program."
  }
];

export default App;